import tanInternalImage from "@/assets/tan-internal.webp";
import { Droplets, Shield, Sparkles } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const features = [
  {
    icon: Droplets,
    title: "Precision Atomizer",
    description:
      "German-engineered spray mechanism delivers the perfect mist every time, preserving your fragrance's integrity.",
  },
  {
    icon: Shield,
    title: "Leak-Proof Design",
    description:
      "Travel with confidence. Our patented seal technology ensures not a single drop escapes.",
  },
  {
    icon: Sparkles,
    title: "Easy Refill",
    description:
      "Simply remove the glass vial to refill. Compatible with any perfume, no adapters needed.",
  },
];

const CraftSection = () => {
  const { ref: imageRef, isVisible: imageVisible } = useScrollReveal();
  const { ref: contentRef, isVisible: contentVisible } = useScrollReveal();

  return (
    <section id="craft" className="py-24 lg:py-32">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image */}
          <div
            ref={imageRef}
            className={`relative transition-all duration-700 ease-out ${
              imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
            }`}
          >
            <div className="absolute -top-6 -left-6 w-full h-full border border-gold/20 rounded-sm" />
            <img
              src={tanInternalImage}
              alt="Luxe Lords atomizer internal mechanism showing Premium Vegan leather case and glass vial"
              className="relative z-10 w-full rounded-sm shadow-elevated"
              width={624}
              height={416}
              loading="lazy"
            />
            <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-gold/10 rounded-full blur-3xl" />
          </div>

          {/* Content */}
          <div
            ref={contentRef}
            className={`transition-all duration-700 ease-out delay-200 ${
              contentVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
            }`}
          >
            <p className="text-luxury text-muted-foreground mb-4">The Craft</p>
            <h2 className="heading-section text-foreground mb-6">
              Engineered for
              <br />
              <span className="text-gold italic">Perfection</span>
            </h2>
            <p className="text-body text-muted-foreground mb-12">
              Each Luxe Lords atomizer marries Premium Vegan leather craftsmanship
              with precision engineering. The result is a travel companion that's
              as beautiful as it is functional.
            </p>

            {/* Features */}
            <div className="space-y-8">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`flex gap-5 transition-all duration-500 ease-out ${
                    contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  }`}
                  style={{ transitionDelay: `${300 + index * 150}ms` }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-foreground mb-2 tracking-wide">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CraftSection;
